/**
 * pan/fly only when a survey is outside the
 * current view, and fit-to-extent when a newly enabled layer is off-screen.
 */

export interface ExtentLike {
  readonly xmin?: number;
  readonly ymin?: number;
  readonly xmax?: number;
  readonly ymax?: number;
  readonly center?: unknown;
  readonly spatialReference?: unknown;
  intersects?: (other: ExtentLike) => boolean;
}

export interface MapViewLike {
  readonly extent?: unknown;
  readonly zoom?: number;
  animation?: {
    stop?: () => void;
    destroy?: () => void;
  } | null;
  goTo: (
    target: unknown,
    options?: {
      readonly duration?: number;
      readonly easing?: string;
      readonly animate?: boolean;
    },
  ) => Promise<unknown>;
}

export interface LayerExtentLike {
  readonly fullExtent?: unknown;
}

export const SURVEY_MAP_FLY_TO_DURATION_MS = 4500;
export const SURVEY_MAP_FLY_TO_EASING = 'ease-in-out' as const;

/**
 * Stop any in-flight MapView goTo animation. Required before removeMany /
 * overlapping goTo 
 */
export function stopMapViewAnimation(
  mapView: MapViewLike | null | undefined,
): void {
  if (!mapView) {
    return;
  }
  const animation = mapView.animation;
  if (!animation) {
    return;
  }
  try {
    if (typeof animation.stop === 'function') {
      animation.stop();
      return;
    }
    if (typeof animation.destroy === 'function') {
      animation.destroy();
    }
  } catch {
    // Animation may already be disposed mid-teardown.
  }
}

function isExtentLike(value: unknown): value is ExtentLike {
  return typeof value === 'object' && value !== null;
}

function readExtentCenter(
  extent: ExtentLike,
): { readonly x: number; readonly y: number } | null {
  if (
    typeof extent.center === 'object' &&
    extent.center !== null &&
    typeof (extent.center as { readonly x?: unknown }).x === 'number' &&
    typeof (extent.center as { readonly y?: unknown }).y === 'number'
  ) {
    return {
      x: (extent.center as { readonly x: number }).x,
      y: (extent.center as { readonly y: number }).y,
    };
  }
  if (
    typeof extent.xmin !== 'number' ||
    typeof extent.xmax !== 'number' ||
    typeof extent.ymin !== 'number' ||
    typeof extent.ymax !== 'number'
  ) {
    return null;
  }
  return {
    x: (extent.xmin + extent.xmax) / 2,
    y: (extent.ymin + extent.ymax) / 2,
  };
}

export function shouldFlyToLayerExtent(
  viewExtent: ExtentLike | null | undefined,
  layerExtent: ExtentLike | null | undefined,
): boolean {
  if (!layerExtent) {
    return false;
  }
  if (!viewExtent) {
    return true;
  }
  if (typeof viewExtent.intersects !== 'function') {
    return true;
  }
  try {
    return !viewExtent.intersects(layerExtent);
  } catch {
    return true;
  }
}

export function resolveSurveyHighlightPanTarget(
  target: unknown,
): { readonly target: unknown } | null {
  if (!isExtentLike(target)) {
    return null;
  }

  if (
    typeof target.center === 'object' &&
    target.center !== null &&
    typeof (target.center as { readonly x?: unknown }).x === 'number' &&
    typeof (target.center as { readonly y?: unknown }).y === 'number'
  ) {
    return { target: target.center };
  }

  const center = readExtentCenter(target);
  if (!center) {
    return null;
  }

  return {
    target: {
      type: 'point',
      x: center.x,
      y: center.y,
      ...(target.spatialReference !== undefined
        ? { spatialReference: target.spatialReference }
        : {}),
    },
  };
}

/**
 * Pan to a survey highlight only when it is outside the current viewport.
 * Preserves zoom
 */
export async function flyToSurveyHighlightIfOutOfView(
  mapView: MapViewLike | null | undefined,
  target: unknown,
  options?: {
    readonly durationMs?: number;
    readonly isCancelled?: () => boolean;
  },
): Promise<void> {
  if (!mapView || target == null) {
    return;
  }

  const targetExtent = isExtentLike(target) ? target : null;
  const viewExtent = isExtentLike(mapView.extent) ? mapView.extent : null;
  if (!targetExtent || !shouldFlyToLayerExtent(viewExtent, targetExtent)) {
    return;
  }

  const duration = options?.durationMs ?? SURVEY_MAP_FLY_TO_DURATION_MS;
  const isCancelled = options?.isCancelled ?? (() => false);
  if (isCancelled()) {
    return;
  }

  const panTarget = resolveSurveyHighlightPanTarget(targetExtent);
  if (!panTarget || isCancelled()) {
    return;
  }

  stopMapViewAnimation(mapView);
  await mapView
    .goTo(panTarget, { duration, easing: SURVEY_MAP_FLY_TO_EASING })
    .catch(() => {});
}

/**
 * Fit to a layer's fullExtent when the current viewport does not intersect it.
 * Used when enabling a survey layer that is off-screen.
 */
export function flyToSurveyLayerIfOutOfView(
  mapView: MapViewLike | null | undefined,
  layer: LayerExtentLike | null | undefined,
  options?: {
    readonly durationMs?: number;
  },
): void {
  if (!mapView || !layer) {
    return;
  }
  const layerExtent = isExtentLike(layer.fullExtent) ? layer.fullExtent : null;
  const viewExtent = isExtentLike(mapView.extent) ? mapView.extent : null;
  if (!shouldFlyToLayerExtent(viewExtent, layerExtent)) {
    return;
  }
  stopMapViewAnimation(mapView);
  void mapView
    .goTo(layerExtent, {
      duration: options?.durationMs ?? SURVEY_MAP_FLY_TO_DURATION_MS,
      easing: SURVEY_MAP_FLY_TO_EASING,
    })
    .catch(() => {});
}
